<template>
  <transition name="dc-modal">
  <div
    v-if="visible"
    class="fixed inset-0 bg-gray-600/50 flex items-center justify-center z-50"
    @click="$emit('close')"
  >
    <div class="dc-modal-panel bg-white rounded-lg shadow-xl w-full max-w-2xl" @click.stop>
      <!-- Header -->
      <div class="flex items-start justify-between px-6 pt-6 pb-4 border-b border-gray-200">
        <div>
          <h3 class="text-lg font-medium text-gray-900">Add Data Connection</h3>
          <p class="mt-1 text-xs text-gray-400">
            Connect Workflow Builder to a data warehouse so its data attributes can
            read from — and write to — your own database.
          </p>
        </div>
        <button class="btn-icon" title="Close" @click="$emit('close')">
          <font-awesome-icon :icon="['fas', 'times']" />
        </button>
      </div>

      <!-- Connection-type tabs -->
      <div class="flex gap-1 px-6 pt-4">
        <button
          v-for="t in types"
          :key="t.key"
          class="px-3 py-2 text-sm font-medium rounded-t-md border-b-2 transition-colors"
          :class="activeType === t.key
            ? 'border-primary-500 text-primary-600'
            : 'border-transparent text-gray-500 hover:text-gray-700'"
          @click="activeType = t.key"
        >
          {{ t.label }}
        </button>
      </div>

      <div class="px-6 py-5 border-t border-gray-200">
        <!-- ── Snowflake (service-account key-pair) ─────────────────────── -->
        <div v-if="activeType === 'snowflake'" class="space-y-4">
          <p class="text-xs text-gray-500">
            Connect to your Snowflake account with a service-account key-pair.
            Credentials are encrypted at rest and used only to run this
            connection's queries.
          </p>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="dc-label">Connection name</label>
              <input v-model.trim="sf.name" type="text" placeholder="e.g. Acme Snowflake" class="dc-input" />
            </div>
            <div>
              <label class="dc-label">Account</label>
              <input v-model.trim="sf.account" type="text" placeholder="xy12345.us-east-1" class="dc-input" />
            </div>
            <div>
              <label class="dc-label">Warehouse</label>
              <input v-model.trim="sf.warehouse" type="text" placeholder="COMPUTE_WH" class="dc-input" />
            </div>
            <div>
              <label class="dc-label">Database</label>
              <input v-model.trim="sf.database" type="text" placeholder="ANALYTICS" class="dc-input" />
            </div>
            <div>
              <label class="dc-label">Role</label>
              <input v-model.trim="sf.role" type="text" placeholder="KAIRON_READER" class="dc-input" />
            </div>
            <div>
              <label class="dc-label">Service-account user</label>
              <input v-model.trim="sf.username" type="text" placeholder="KAIRON_SVC" class="dc-input" />
            </div>
          </div>
          <div>
            <label class="dc-label">Private key (PEM)</label>
            <textarea
              v-model="sf.private_key"
              rows="4"
              spellcheck="false"
              placeholder="-----BEGIN PRIVATE KEY-----&#10;…&#10;-----END PRIVATE KEY-----"
              class="dc-input font-mono text-xs"
            ></textarea>
          </div>
        </div>

        <!-- ── JDBC ─────────────────────────────────────────────────────── -->
        <div v-else-if="activeType === 'jdbc'" class="space-y-4">
          <p class="text-xs text-gray-500">
            Paste the JDBC URL your database team gives out, plus a read-only user. Kairon reads the host,
            port and database from the URL. The connection always uses TLS with certificate verification,
            and the password is kept in AWS Secrets Manager.
          </p>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="dc-label">Connection name</label>
              <input v-model.trim="jdbc.name" type="text" placeholder="e.g. Acme Postgres (JDBC)" class="dc-input" />
            </div>
            <div>
              <label class="dc-label">Driver class</label>
              <input :value="jdbcEngine ? jdbcEngine.jdbc_driver_class : 'Set by the URL'" type="text" class="dc-input dc-readonly" readonly />
            </div>
          </div>
          <div>
            <label class="dc-label">JDBC URL</label>
            <input
              v-model.trim="jdbc.url"
              type="text"
              spellcheck="false"
              :placeholder="jdbcPlaceholder"
              class="dc-input font-mono text-xs"
            />
            <p class="dc-hint">Supported: {{ jdbcSupported }}. Leave the username and password out of the URL.</p>
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="dc-label">Username</label>
              <input v-model.trim="jdbc.username" type="text" placeholder="kairon_reader" class="dc-input" />
            </div>
            <div>
              <label class="dc-label">Password</label>
              <input v-model="jdbc.password" type="password" autocomplete="new-password" class="dc-input" />
            </div>
          </div>
        </div>

        <!-- ── ODBC ─────────────────────────────────────────────────────── -->
        <div v-else-if="activeType === 'odbc'" class="space-y-4">
          <p class="text-xs text-gray-500">
            Give the server details and a read-only user. Kairon builds the ODBC connection itself: always
            TLS with certificate verification (SSL mode verify-full), read-only, with a statement timeout.
            The password is kept in AWS Secrets Manager.
          </p>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="dc-label">Connection name</label>
              <input v-model.trim="odbc.name" type="text" placeholder="e.g. Acme Postgres (ODBC)" class="dc-input" />
            </div>
            <div>
              <label class="dc-label">Database engine</label>
              <select v-model="odbc.engine" class="dc-input" @change="applyEnginePort">
                <option v-for="e in engines" :key="e.key" :value="e.key">{{ e.label }}</option>
              </select>
            </div>
            <div>
              <label class="dc-label">ODBC driver</label>
              <input :value="odbcEngine ? odbcEngine.odbc_driver : ''" type="text" class="dc-input dc-readonly" readonly />
            </div>
            <div>
              <label class="dc-label">SSL mode</label>
              <input value="verify-full" type="text" class="dc-input dc-readonly" readonly />
            </div>
            <div>
              <label class="dc-label">Server (host)</label>
              <input v-model.trim="odbc.host" type="text" placeholder="db.example.com" class="dc-input" />
            </div>
            <div>
              <label class="dc-label">Port</label>
              <input v-model.number="odbc.port" type="number" min="1" max="65535" class="dc-input" />
            </div>
            <div>
              <label class="dc-label">Database</label>
              <input v-model.trim="odbc.database" type="text" placeholder="analytics" class="dc-input" />
            </div>
            <div>
              <label class="dc-label">Username (UID)</label>
              <input v-model.trim="odbc.username" type="text" placeholder="kairon_reader" class="dc-input" />
            </div>
          </div>
          <div>
            <label class="dc-label">Password (PWD)</label>
            <input v-model="odbc.password" type="password" autocomplete="new-password" class="dc-input" />
          </div>
        </div>

        <p v-if="!hostsOpen && activeType !== 'snowflake'" class="mt-3 text-xs text-gray-500">
          Kairon only connects to database hosts it has been opened to. You can save this connection now;
          Test will fail until the host is allowed.
        </p>
        <p v-if="error" class="mt-3 text-xs text-red-dark">{{ error }}</p>
        <p v-if="testResult" class="mt-3 text-xs" :class="testResult.ok ? 'text-green-dark' : 'text-red-dark'">
          {{ testResult.ok ? '✓ Connection succeeded.' : ('✕ ' + testResult.message) }}
        </p>
      </div>

      <!-- Footer -->
      <div class="flex justify-end gap-2 px-6 py-4 border-t border-gray-200">
        <button class="btn-secondary" @click="$emit('close')">Cancel</button>
        <button class="btn-secondary" :disabled="!canSave || testing" @click="handleTest">
          {{ testing ? 'Testing…' : 'Test' }}
        </button>
        <button class="btn-accent" :disabled="!canSave || saving" @click="handleSave">
          {{ saving ? 'Saving…' : 'Add connection' }}
        </button>
      </div>
    </div>
  </div>
  </transition>
</template>

<script>
import axios from 'axios';

const SOURCE_BASE = '/api/impact_analytics/data-sources/';

const DEFAULT_ENGINES = [{
  key: 'postgresql', label: 'PostgreSQL', default_port: 5432,
  odbc_driver: 'PostgreSQL Unicode', jdbc_subprotocol: 'postgresql', jdbc_driver_class: 'org.postgresql.Driver',
}];
const emptyJdbc = () => ({ name: '', url: '', username: '', password: '' });
const emptyOdbc = () => ({ name: '', engine: 'postgresql', host: '', port: 5432, database: '', username: '', password: '' });

export default {
  name: 'AddDataConnectionModal',

  props: {
    visible: { type: Boolean, default: false },
  },

  emits: ['close', 'created'],

  data() {
    return {
      activeType: 'snowflake',
      types: [
        { key: 'snowflake', label: 'Snowflake' },
        { key: 'jdbc', label: 'JDBC' },
        { key: 'odbc', label: 'ODBC' },
      ],
      sf: { name: '', account: '', warehouse: '', database: '', role: '', username: '', private_key: '' },
      jdbc: emptyJdbc(),
      odbc: emptyOdbc(),
      hostsOpen: true,
      engines: DEFAULT_ENGINES,
      testing: false,
      saving: false,
      testResult: null,
      error: null,
    };
  },

  computed: {
    odbcEngine() {
      return this.engines.find((e) => e.key === this.odbc.engine) || null;
    },
    jdbcEngine() {
      const match = /^jdbc:([a-z0-9]+):/.exec(this.jdbc.url || '');
      return match ? this.engines.find((e) => e.jdbc_subprotocol === match[1]) || null : null;
    },
    jdbcSupported() {
      return this.engines.map((e) => `jdbc:${e.jdbc_subprotocol}`).join(', ');
    },
    jdbcPlaceholder() {
      const e = this.engines[0] || DEFAULT_ENGINES[0];
      return `jdbc:${e.jdbc_subprotocol}://db.example.com:${e.default_port}/analytics`;
    },
    canSave() {
      if (this.activeType === 'jdbc') {
        const j = this.jdbc;
        return !!(j.name && j.url && j.username && j.password);
      }
      if (this.activeType === 'odbc') {
        const o = this.odbc;
        return !!(o.name && o.engine && o.host && o.port && o.database && o.username && o.password);
      }
      const s = this.sf;
      return s.name && s.account && s.warehouse && s.database && s.role && s.username && s.private_key;
    },
  },

  watch: {
    visible(open) {
      if (open) this.reset();
    },
  },

  methods: {
    reset() {
      this.activeType = 'snowflake';
      this.sf = { name: '', account: '', warehouse: '', database: '', role: '', username: '', private_key: '' };
      this.jdbc = emptyJdbc();
      this.odbc = emptyOdbc();
      this.loadCapabilities();
      this.testing = false;
      this.saving = false;
      this.testResult = null;
      this.error = null;
    },

    async loadCapabilities() {
      try {
        const { data } = await axios.get(`${SOURCE_BASE}capabilities/`);
        const odbc = data?.jdbc_odbc || {};
        this.hostsOpen = !!odbc.enabled;
        if (odbc.engines && odbc.engines.length) this.engines = odbc.engines;
      } catch {
        this.hostsOpen = false;
      }
    },

    applyEnginePort() {
      const engine = this.engines.find((e) => e.key === this.odbc.engine);
      if (engine) this.odbc.port = engine.default_port;
    },

    _jdbcFields() {
      return {
        connection_kind: 'jdbc_odbc',
        protocol: 'jdbc',
        jdbc_url: this.jdbc.url,
        db_username: this.jdbc.username,
        db_password: this.jdbc.password,
      };
    },

    _odbcFields() {
      return {
        connection_kind: 'jdbc_odbc',
        protocol: 'odbc',
        engine: this.odbc.engine,
        host: this.odbc.host,
        port: this.odbc.port,
        database: this.odbc.database,
        db_username: this.odbc.username,
        db_password: this.odbc.password,
      };
    },

    _payload() {
      if (this.activeType === 'jdbc') {
        return { name: this.jdbc.name, ...this._jdbcFields(), is_system: false };
      }
      if (this.activeType === 'odbc') {
        return { name: this.odbc.name, ...this._odbcFields(), is_system: false };
      }
      return {
        name: this.sf.name,
        connection_kind: 'snowflake',
        account: this.sf.account,
        warehouse: this.sf.warehouse,
        database: this.sf.database,
        role: this.sf.role,
        snowflake_user: this.sf.username,
        private_key: this.sf.private_key,
        is_system: false,
      };
    },

    async handleTest() {
      this.testing = true;
      this.testResult = null;
      this.error = null;
      try {
        const forms = { jdbc: this._jdbcFields, odbc: this._odbcFields };
        const body = forms[this.activeType] ? forms[this.activeType]() : {
          connection_kind: 'snowflake',
          account: this.sf.account,
          snowflake_user: this.sf.username,
          private_key: this.sf.private_key,
          warehouse: this.sf.warehouse,
          database: this.sf.database,
          role: this.sf.role,
        };
        const { data: t } = await axios.post(`${SOURCE_BASE}test-connection/`, body);
        this.testResult = { ok: !!t.success, message: t.message || (t.success ? 'Connection successful.' : 'Connection failed.') };
      } catch (e) {
        this.testResult = { ok: false, message: e.response?.data?.message || e.response?.data?.error || 'Test failed.' };
      } finally {
        this.testing = false;
      }
    },

    async handleSave() {
      this.saving = true;
      this.error = null;
      try {
        const { data } = await axios.post(SOURCE_BASE, this._payload());
        this.$emit('created', data);
        this.$emit('close');
      } catch (e) {
        const d = e.response?.data;
        this.error = d?.error || (d && typeof d === 'object' ? Object.values(d).flat()[0] : null) || 'Could not add the connection.';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.dc-label { display: block; font-size: 11px; font-weight: 500; color: #6b7280; margin-bottom: 4px; }
.dc-input {
  width: 100%; box-sizing: border-box; border: 1px solid #d1d5db; border-radius: 6px;
  padding: 7px 9px; font-size: 13px; color: #1f2937; background: #fff;
  transition: border-color .12s ease, box-shadow .12s ease;
}
.dc-readonly { background: #f9fafb; color: #6b7280; }
.dc-hint { margin-top: 4px; font-size: 11px; color: #9ca3af; }
.dc-input:focus { outline: none; border-color: var(--color-blue-dark, #4573D2); box-shadow: 0 0 0 2px rgba(69,115,210,.25); }

.dc-modal-enter-active, .dc-modal-leave-active { transition: opacity 0.18s ease; }
.dc-modal-enter-from, .dc-modal-leave-to { opacity: 0; }
.dc-modal-enter-active .dc-modal-panel { transition: transform 0.2s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.2s ease; }
.dc-modal-enter-from .dc-modal-panel { transform: translateY(8px) scale(0.97); opacity: 0; }
@media (prefers-reduced-motion: reduce) {
  .dc-modal-enter-active, .dc-modal-leave-active,
  .dc-modal-enter-active .dc-modal-panel { transition: none; }
}
</style>
